import { ColumnOutlined } from '@vindr/icons';
import { DefinedColumns } from '@vindr/services/interfaces';
import { ActionIcon } from '@vindr/ui';
import { Pick, addToArray, sortAlphaText } from '@vindr/utils/helpers/common.helper';
import { localStorageAction } from '@vindr/utils/helpers/storage';
import { SetState } from '@vindr/utils/types';
import { Button, Checkbox, Popover, Tooltip } from 'antd';
import isEmpty from 'lodash/isEmpty';
import { CSSProperties, useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { DragListViewProps } from 'react-drag-listview';
import { useTranslation } from 'react-i18next';
import useAntdResizableHeader from 'use-antd-resizable-header';

type Columns<T> = DefinedColumns<T> | any;

function useDragResizeColumn<T>(
  performedColumns: T[],
  settingKey: string,
  defaultKey: string,
  setContextMenuState?: (state: any | ((prevState: any) => any)) => void,
  setSelectedRowKeys?: SetState<any>
) {
  const { t } = useTranslation('Vindoc');

  const [visibleColumns, setVisibleColumns] = useState<Columns<T>>();

  const getDefaultCol = useCallback(
    (): Columns<T> => localStorageAction.get(defaultKey)?.resizableColumns,
    [defaultKey]
  );

  const getSettingCol = useCallback(
    (): Columns<T> => localStorageAction.get(settingKey)?.resizableColumns,
    [settingKey]
  );

  const getColSetting = useCallback(
    (): Columns<T> => getSettingCol() || getDefaultCol(),
    [getDefaultCol, getSettingCol]
  );

  useLayoutEffect(() => {
    setVisibleColumns(getColSetting());
  }, [getColSetting]);

  const showHideColumns = useCallback(
    (resetColumns) => ({
      title: (
        <Popover
          placement="bottomRight"
          title={t('Modify Columns')}
          trigger="click"
          content={
            <ColumnsContent<T>
              {...{
                visibleColumns,
                setVisibleColumns,
                performedColumns,
                settingKey,
                getDefaultCol,
                resetColumns,
              }}
            />
          }
        >
          <Button size="small">
            <ColumnOutlined className={'text-xl'} color={'white'} />
          </Button>
        </Popover>
      ),
      key: 'showHideColumns',
      fixed: 'right',
      align: 'center',
      width: 50,
      render: (record) => {
        if (typeof setContextMenuState !== 'function') return null;
        return (
          <div className="flex justify-center">
            <ActionIcon.Options
              style={{ border: 'none' }}
              onClick={(e: React.MouseEvent) => {
                e.preventDefault();
                e.stopPropagation();
                setContextMenuState({
                  visible: true,
                  x: e.clientX,
                  y: e.clientY,
                  currentRecord: record,
                  selectedRecords: [record],
                });
                setSelectedRowKeys?.([record]);
              }}
            />
          </div>
        );
      },
    }),
    [getDefaultCol, performedColumns, setContextMenuState, settingKey, t, visibleColumns]
  );

  const { components, resizableColumns, tableWidth, resetColumns } = useAntdResizableHeader({
    columns: useMemo(() => {
      if (!performedColumns?.length) return;
      const defaultColumns = isEmpty(visibleColumns) ? getColSetting() : visibleColumns;

      const configColumns = [];
      for (const col of defaultColumns) {
        const foundColumn = performedColumns.find((item) => item.key === col.key);
        if (foundColumn) configColumns.push(foundColumn);
      }

      //? Add empty col at last
      if (!configColumns.some((x) => x.key === 'empty')) {
        configColumns.push({
          key: 'empty',
          dataIndex: 'empty',
          lock: true,
        });
      }

      return configColumns;
    }, [getColSetting, performedColumns, visibleColumns]),
    columnsState: { persistenceKey: settingKey, persistenceType: 'localStorage' },
    minConstraints: 50,
    defaultWidth: 100,
    tooltipRender: (props) => <Tooltip {...props} />,
  });

  const dragProps: DragListViewProps = useMemo(
    () => ({
      onDragEnd(fromIndex, toIndex) {
        if (fromIndex === toIndex) return;
        if (fromIndex === 1 && toIndex < fromIndex) return;
        const updatedColumns = [...visibleColumns];
        const item = updatedColumns.splice(fromIndex - 1, 1)[0];
        updatedColumns.splice(toIndex - 1, 0, item);

        localStorageAction.set(settingKey, { resizableColumns: updatedColumns });
        setVisibleColumns(updatedColumns);
      },
      nodeSelector: 'th',
      handleSelector: '.resizable-title, span[title]',
      lineClassName: 'global-drag-line',
      ignoreSelector: 'react-resizable-handle, th[data-index="empty"]',
    }),
    [settingKey, visibleColumns]
  );

  useEffect(() => {
    const headers = document.querySelectorAll('.ant-table-header th');

    headers.forEach((header) => {
      header.addEventListener('dragstart', dragStartHandler);
      header.addEventListener('dragend', dragEndHandler);
    });

    return () => {
      headers.forEach((header) => {
        header.removeEventListener('dragstart', dragStartHandler);
        header.removeEventListener('dragend', dragEndHandler);
      });
    };
  }, []);

  return {
    components,
    dragProps,
    getDefaultCol,
    resetColumns,
    // resizableColumns: [...resizableColumns, showHideColumns(resetColumns)],
    resizableColumns,
    setVisibleColumns,
    tableWidth,
    visibleColumns,
  };
}

export default useDragResizeColumn;

const ColumnsContent = <T,>({
  visibleColumns,
  setVisibleColumns,
  performedColumns,
  settingKey,
  getDefaultCol,
  resetColumns,
}: any) => {
  const { t } = useTranslation('Vindoc');

  const handleResetColumns = useCallback(() => {
    const defaultCol = getDefaultCol();
    const sortedPerformedColumns = sortCol(defaultCol, performedColumns);
    const columnsToSet = defaultCol || getPerformedColumns(sortedPerformedColumns);

    // columnsToSet.push(Pick(columnsToSet, ['key', 'dataIndex', 'width']));

    localStorageAction.set(settingKey, { resizableColumns: columnsToSet });
    setVisibleColumns(columnsToSet); //? Reset position column

    resetColumns(); //? Reset width column
  }, [getDefaultCol, settingKey, setVisibleColumns, resetColumns]);

  const handleVisibleChange = useCallback(
    <T,>(column: Columns<T>[number]) => {
      const isColumnVisible = visibleColumns?.some((visCol) => visCol.key === column.key);
      const updatedVisibleColumns = isColumnVisible
        ? visibleColumns.filter((visCol) => visCol.key !== column.key)
        : [...visibleColumns, column];

      const moveEmptyToLast = updatedVisibleColumns.sort((a, b) => {
        if (a.dataIndex === 'empty' && a.key === 'empty') return 1; // Move 'empty' to the end
        if (b.dataIndex === 'empty' && b.key === 'empty') return -1; // Move 'empty' to the end
        return 0; // No change in order
      });

      setVisibleColumns(moveEmptyToLast);
      localStorageAction.set(settingKey, { resizableColumns: moveEmptyToLast });
    },
    [setVisibleColumns, settingKey, visibleColumns]
  );

  return (
    <div className="flex flex-col" style={{ maxHeight: 'min(400px, 80vh)' }}>
      <div className="flex-1" style={{ overflowY: 'auto' }}>
        {sortCol(visibleColumns, performedColumns).map((column) => (
          <div key={column.title}>
            <Checkbox
              checked={visibleColumns?.some((col) => col.key === column.key)}
              disabled={column?.lock}
              onChange={() => handleVisibleChange<T>(column)}
            >
              {column.title}
            </Checkbox>
          </div>
        ))}
      </div>
      <div className="mt-2 flex items-center justify-between">
        <Button className="reset-button" size="small" onClick={handleResetColumns}>
          {t('Reset')}
        </Button>
        <div>{`${visibleColumns.filter((x) => x.key !== 'empty')?.length} / ${
          performedColumns?.length
        }`}</div>
      </div>
    </div>
  );
};

export function getPerformedColumns<T>(performedColumns): Columns<T> {
  return performedColumns.map(PickToLocalStorage);
}

export function PickToLocalStorage(column) {
  return Pick(column, ['title', 'key', 'dataIndex', 'width']);
}

function sortCol(savedColumns, performedColumns) {
  savedColumns = addToArray(savedColumns, []);
  performedColumns = addToArray(performedColumns, []);

  const savedKeys = savedColumns.map((column) => column.key);
  performedColumns = performedColumns?.length
    ? sortAlphaText(performedColumns, 'asc', (item) => item?.title || '')
    : performedColumns;
  return performedColumns.sort((a, b) => {
    const indexA = savedKeys.indexOf(a.key);
    const indexB = savedKeys.indexOf(b.key);
    if (indexA === -1) return 1;
    if (indexB === -1) return -1;
    return indexA - indexB;
  });
}

function createGhostDiv(target: HTMLElement) {
  if (target?.nodeType !== Node.ELEMENT_NODE) return;

  const heightTable = target?.closest?.('.ant-table-container')?.clientHeight;

  const div = document.createElement('div');
  Object.assign(div.style, {
    position: 'absolute',
    top: '0px',
    left: '0px',
    border: '2px solid rgb(57 194 215 / 1)',
    outline: 'none',
    width: `${target.offsetWidth}px`,
    height: `${heightTable || target.offsetHeight * 5}px`,
    background: 'rgba(44, 108, 151, 0.22)',
    boxShadow: '0 4px 30px rgba(0, 0, 0, 0.1)',
    backDropFilter: 'blur(5px)',
    webkitBackDropFilter: 'blur(5px)',
    transition: 'border 1s ease-in-out',
  } as CSSProperties);
  div.classList.add('ghost-background');
  return div;
}

const dragStartHandler = (e) => {
  if (!e.target || e?.target?.nodeType !== Node.ELEMENT_NODE) return;

  const div = createGhostDiv(e.target);
  e.target.appendChild(div);
  e.dataTransfer.setDragImage(div, 10, 10);
};
const dragEndHandler = () => {
  const ghostImage = document.querySelector('th .ghost-background');
  if (ghostImage && ghostImage?.nodeType === Node.ELEMENT_NODE) ghostImage.remove();
};

const EMPTY_COLUMN = 1;
